import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, ExternalLink, Video } from "lucide-react";
import { format } from "date-fns";
import eabLogo from "@/assets/eab-logo-horizontal.png";
import { AttachmentDisplay } from "@/components/announcements/AttachmentDisplay";
import { Attachment, getVideoEmbedUrl } from "@/hooks/useAnnouncementAttachments";
import { usePublicEvents } from "@/hooks/useEvents";
import Footer from "@/components/layout/Footer";
import { getStorageUrl } from "@/lib/storage";
import { formatEventTime, getTimezoneLabel } from "@/lib/eventTimezone";

interface AnnouncementWithAttachments {
  id: string;
  title: string;
  content: string;
  is_active: boolean;
  is_public: boolean;
  created_at: string;
  created_by: string | null;
  updated_at: string;
  attachments: Attachment[];
}

const News = () => {
  const { data: announcements, isLoading: announcementsLoading } = useQuery({
    queryKey: ["public-announcements-with-attachments"],
    queryFn: async () => {
      const { data: announcementsData, error: announcementsError } = await supabase
        .from("announcements")
        .select("*")
        .eq("is_active", true)
        .eq("is_public", true)
        .order("created_at", { ascending: false });

      if (announcementsError) throw announcementsError;

      // Fetch attachments for all announcements
      const announcementIds = announcementsData.map(a => a.id);
      if (announcementIds.length === 0) return [];

      const { data: attachmentsData, error: attachmentsError } = await supabase
        .from("announcement_attachments")
        .select("*")
        .in("announcement_id", announcementIds);

      if (attachmentsError) throw attachmentsError;

      // Combine announcements with their attachments
      return announcementsData.map(announcement => ({
        ...announcement,
        attachments: (attachmentsData || []).filter(a => a.announcement_id === announcement.id) as Attachment[],
      })) as AnnouncementWithAttachments[];
    },
  });

  const { data: events, isLoading: eventsLoading } = usePublicEvents();

  const isLoading = announcementsLoading || eventsLoading;

  const EventCard = ({ event }: { event: (typeof events)[number] }) => {
    const eventTimezone = event.timezone || "America/Jamaica";
    const images = event.attachments?.filter((a) => a.attachment_type === "image") || [];
    const videos = event.attachments?.filter((a) => a.attachment_type === "video") || [];
    
    // Find featured image or use first image
    const featuredImage = images.find((img) => img.is_featured) || images[0];

    return (
      <Card className="hover:shadow-lg transition-shadow overflow-hidden">
        {featuredImage && (
          <div className="relative aspect-video">
            <img
              src={getStorageUrl("attachments", featuredImage.url) || featuredImage.url}
              alt={event.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <CardHeader>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <Calendar className="h-4 w-4" />
            {formatEventTime(event.event_date, eventTimezone, "EEEE, MMMM d, yyyy 'at' h:mm a")}
            <span className="text-xs">({getTimezoneLabel(eventTimezone)})</span>
          </div>
          <CardTitle className="text-2xl">{event.title}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {event.description && (
            <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed">
              {event.description}
            </p>
          )}

          {videos.length > 0 && (
            <div className="space-y-2">
              {videos.map((video) => {
                const embedUrl = getVideoEmbedUrl(video.url);
                if (embedUrl) {
                  return (
                    <div key={video.id} className="aspect-video rounded-md overflow-hidden">
                      <iframe
                        src={embedUrl}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  );
                }
                return (
                  <a
                    key={video.id}
                    href={video.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-primary hover:underline"
                  >
                    <Video className="h-4 w-4" />
                    Watch Video
                  </a>
                );
              })}
            </div>
          )}

          {event.registration_link && (
            <Button asChild className="w-full">
              <a
                href={event.registration_link}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Register for Event
              </a>
            </Button>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={eabLogo} alt="EAB Connect" className="h-10" />
          </Link>
          <nav className="flex items-center gap-4">
            <Button variant="ghost" asChild>
              <Link to="/">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/auth">Sign In</Link>
            </Button>
            <Button asChild className="bg-accent hover:bg-accent/90 text-accent-foreground">
              <Link to="/auth?tab=signup">Get Started</Link>
            </Button>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-center mb-2">News & Events</h1>
          <p className="text-muted-foreground text-center mb-12">
            Stay informed about the latest happenings at EAB Connect
          </p>

          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardHeader>
                    <div className="h-6 bg-muted rounded w-3/4"></div>
                  </CardHeader>
                  <CardContent>
                    <div className="h-4 bg-muted rounded w-full mb-2"></div>
                    <div className="h-4 bg-muted rounded w-2/3"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="space-y-12">
              {/* Upcoming Events Section */}
              {events && events.length > 0 && (
                <section>
                  <h2 className="text-2xl font-semibold mb-6">Upcoming Events</h2>
                  <div className="space-y-6">
                    {events.map((event) => (
                      <EventCard key={event.id} event={event} />
                    ))}
                  </div>
                </section>
              )}

              {/* Announcements Section */}
              {announcements && announcements.length > 0 && (
                <section>
                  <h2 className="text-2xl font-semibold mb-6">Announcements</h2>
                  <div className="space-y-6">
                    {announcements.map((announcement) => (
                      <Card key={announcement.id} className="hover:shadow-lg transition-shadow">
                        <CardHeader>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                            <Calendar className="h-4 w-4" />
                            {format(new Date(announcement.created_at), "MMMM d, yyyy")}
                          </div>
                          <CardTitle className="text-2xl">{announcement.title}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed">
                            {announcement.content}
                          </p>
                          <AttachmentDisplay attachments={announcement.attachments} />
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </section>
              )}

              {/* Empty state */}
              {(!events || events.length === 0) && (!announcements || announcements.length === 0) && (
                <Card className="text-center py-12">
                  <CardContent>
                    <p className="text-muted-foreground">No news or events at this time. Check back soon!</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default News;
